import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { sortBy } from 'lodash-es';

import { ActionStyledAsButton } from '../../../buttons/ActionStyledAsButton';
import useFormatMessage from '../../../../hooks/useFormatMessage';
import { SlideViewerContext } from '../../../slideviewer/SlideViewerContextProvider';

// eslint-disable-next-line import/no-cycle
import { dataTestIds } from '../../../../utils/constants/dataTestIds';
import { MultipleChoiceQuestionWrapper } from './MultipleChoiceQuestionWrapper';
import {
  doesUserHaveResults,
  getAnswersForQuestion,
  getButtonText,
  getCorrectAnswersForQuestion,
  getUserAnswers,
  mergeAnswers,
  showSuccessAnimationIfAllIsCorrect,
} from './helpers';
import SlideViewerMultipleChoiceQuestionCards from './SlideViewerMultipleChoiceQuestionCards';

const SlideViewerMultipleChoiceQuestion = memo(
  ({
    canSubmit,
    question,
    showAnswers = false,
    showAnswersInSidebar = false,
  }) => {
    const t = useFormatMessage();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const {
      canOverrideAnswers,
      isCodefever,
      isSubmittingAnswers,
      isTeacher,
      onSubmitQuestionAnswers,
      refetchAnswers,
      selectedPeriodIsNotActivePeriod,
      setShowConfirmationDialog,
      setSubmitAnswer,
      submittedQuestionAnswers,
    } = useContext(SlideViewerContext);

    // Memoize the answers processing
    const { answersForThisQuestion, correctAnswerIds, userAnswers } =
      useMemo(() => {
        const answers = getAnswersForQuestion(
          question,
          submittedQuestionAnswers,
        );
        const correctIds = getCorrectAnswersForQuestion(answers);
        const userAnswers = getUserAnswers(answers);
        return {
          answersForThisQuestion: answers,
          correctAnswerIds: correctIds,
          userAnswers,
        };
      }, [question, submittedQuestionAnswers]);

    // Memoize question answers to prevent unnecessary re-renders
    const questionAnswers = useMemo(
      () => sortBy(question.questionAnswersMultipleChoice, 'id'),
      [question.questionAnswersMultipleChoice],
    );

    const [answerIds, setAnswerIds] = useState(userAnswers || []);

    // Use the memoized questionAnswers instead of sorting on each render
    const [answersToMap, setAnswersToMap] = useState(questionAnswers);

    const handleAnswerSelection = useCallback((selectedAnswerIds) => {
      setAnswerIds(selectedAnswerIds);
    }, []);

    const onSubmit = useCallback(
      (answers) => {
        setIsSubmitting(true);
        setHasSubmitted(true);

        onSubmitQuestionAnswers(question, answers)
          .then((questions) => {
            const serverAnswers = sortBy(
              questions?.questionAnswersMultipleChoice,
              'id',
            );

            if (!isTeacher) {
              const mergedAnswers = mergeAnswers(answersToMap, serverAnswers);
              setAnswersToMap(mergedAnswers);
            }

            showSuccessAnimationIfAllIsCorrect(
              questions,
              answers,
              canOverrideAnswers,
            );
            refetchAnswers();
          })
          .catch((error) => {
            console.error('Error submitting answer:', error);
            // Revert to the original state on error
            setAnswersToMap(questionAnswers);
          })
          .finally(() => {
            setIsSubmitting(false);
            setShowConfirmationDialog(false);
            setSubmitAnswer(null);
          });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        question,
        questionAnswers,
        canOverrideAnswers,
        isTeacher,
        onSubmitQuestionAnswers,
        refetchAnswers,
        setShowConfirmationDialog,
        setSubmitAnswer,
      ],
    );

    // Memoize computed values
    const { disableSubmitButton, hasAlreadySubmittedAndCanNotOverride } =
      useMemo(() => {
        const hasChosenMore = false; // As per your comment about TR-2377
        const noAnswers = answerIds?.length === 0;
        const alreadySubmitted =
          Boolean(correctAnswerIds?.length) && !canOverrideAnswers;

        return {
          disableSubmitButton:
            !canSubmit ||
            hasChosenMore ||
            alreadySubmitted ||
            noAnswers ||
            isSubmitting,
          hasAlreadySubmittedAndCanNotOverride: alreadySubmitted,
          hasChosenMoreAnswersThanAllowed: hasChosenMore,
          hasNoAnswersSelected: noAnswers,
        };
      }, [
        answerIds,
        canOverrideAnswers,
        canSubmit,
        correctAnswerIds,
        isSubmitting,
      ]);

    // Memoize derived values
    const { buttonText, hasResults } = useMemo(() => {
      const hasTeacherSelection =
        isTeacher &&
        hasSubmitted &&
        answersToMap.some((answer) =>
          answerIds.includes(parseInt(answer.id, 10)),
        );

      return {
        buttonText: getButtonText(canOverrideAnswers, canSubmit, t),
        hasResults: doesUserHaveResults({
          canOverrideAnswers,
          canSubmit,
          hasSelectedAnAnswerAsTeacher: hasTeacherSelection,
          selectedPeriodIsNotActivePeriod,
          showAnswers,
          showAnswersInSidebar,
          userAnswers,
        }),
        hasSelectedAnAnswerAsTeacher: hasTeacherSelection,
      };
    }, [
      isTeacher,
      hasSubmitted,
      answersToMap,
      answerIds,
      canOverrideAnswers,
      canSubmit,
      t,
      selectedPeriodIsNotActivePeriod,
      showAnswers,
      showAnswersInSidebar,
      userAnswers,
    ]);

    // Handle isSubmitting state changes
    useEffect(() => {
      if (isSubmittingAnswers !== isSubmitting) {
        setIsSubmitting(isSubmittingAnswers);
      }
    }, [isSubmitting, isSubmittingAnswers]);

    // Setup refetch timer when submitting
    useEffect(() => {
      let timeout = null;

      if (isSubmitting) {
        timeout = setTimeout(() => {
          refetchAnswers();
        }, 5000);
      }

      return () => {
        if (timeout) clearTimeout(timeout);
      };
    }, [isSubmitting, refetchAnswers]);

    // Update answersToMap when answersForThisQuestion changes or for teachers showing answers
    useEffect(() => {
      if (answersForThisQuestion) {
        const sortedAnswers = sortBy(
          answersForThisQuestion?.questionAnswersMultipleChoice || [],
          'id',
        );
        setAnswersToMap(mergeAnswers(answersToMap, sortedAnswers));
      } else if (isTeacher && showAnswersInSidebar) {
        // For teachers in sidebar, use the original question answers when no submitted answers exist
        setAnswersToMap(questionAnswers);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      answersForThisQuestion,
      isTeacher,
      showAnswersInSidebar,
      questionAnswers,
    ]);

    // Update answerIds when submittedQuestionAnswers change
    useEffect(() => {
      if (userAnswers?.length) {
        // Only update if there are new answers that aren't already in answerIds
        const newAnswers = userAnswers.filter((id) => !answerIds.includes(id));
        if (newAnswers.length > 0) {
          setAnswerIds((prev) => [...prev, ...newAnswers]);
        }
      }
    }, [userAnswers, answerIds]);

    return (
      <MultipleChoiceQuestionWrapper
        hasResults={hasAlreadySubmittedAndCanNotOverride}
        isCodefever={isCodefever}
      >
        <SlideViewerMultipleChoiceQuestionCards
          answerIds={answerIds}
          answersToMap={answersToMap}
          disableSubmitButton={disableSubmitButton}
          hasResults={hasResults}
          hasSubmitted={hasSubmitted}
          onAnswerSelect={handleAnswerSelection}
          setSubmitAnswer={setSubmitAnswer}
          showAnswersInSidebar={showAnswersInSidebar}
        />
        {!showAnswersInSidebar && (
          <div className="mt-4 w-full flex justify-center mb-1">
            <ActionStyledAsButton
              disabled={disableSubmitButton}
              iconBefore={PaperAirplaneIcon}
              loading={isSubmitting}
              onClick={() => onSubmit(answerIds)}
              test={
                dataTestIds.component.slideviewer.slide.button
                  .submitMultipleChoiceAnswer
              }
            >
              {buttonText}
            </ActionStyledAsButton>
          </div>
        )}
      </MultipleChoiceQuestionWrapper>
    );
  },
);

SlideViewerMultipleChoiceQuestion.displayName =
  'SlideViewerMultipleChoiceQuestion';

export default SlideViewerMultipleChoiceQuestion;
